<template>
  <div class="acuity-level-view">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <h1>Acuity Level Details</h1>
        <div class="action-buttons">
          <button
            v-if="$canAccess('PatientActivityLevelEdit')"
            class="btn btn-primary me-2"
            @click="$router.push({ name: 'PatientActivityLevelEdit', params: { id: acuityLevel.id } })"
          >
            <i class="fas fa-edit"></i> Edit
          </button>
          <button
            class="btn btn-secondary"
            @click="$router.push({ name: 'PatientActivityLevelList' })"
          >
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Details Card -->
      <div v-if="!loading && acuityLevel" class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ acuityLevel.name }}</h5>
            <span 
              class="badge" 
              :class="getLevelBadgeClass(acuityLevel.level)"
            >
              Level {{ acuityLevel.level }}
            </span>
          </div>
        </div>
        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-6">
              <h6 class="detail-label">Code</h6>
              <p class="detail-value">{{ acuityLevel.code }}</p>
            </div>
            <div class="col-md-6">
              <h6 class="detail-label">Nurse-Patient Ratio</h6>
              <p class="detail-value">1:{{ acuityLevel.nurse_patient_ratio }}</p>
            </div>
          </div>
          
          <div class="row mb-4">
            <div class="col-md-6">
              <h6 class="detail-label">Status</h6>
              <p class="detail-value">
                <span 
                  class="badge" 
                  :class="acuityLevel.is_active ? 'bg-success' : 'bg-danger'"
                >
                  {{ acuityLevel.is_active ? 'Active' : 'Inactive' }}
                </span>
              </p>
            </div>
            <div class="col-md-6">
              <h6 class="detail-label">Created</h6>
              <p class="detail-value">{{ $formatDateTime(acuityLevel.created_at) }}</p>
            </div>
          </div>
          
          <div class="row">
            <div class="col-12">
              <h6 class="detail-label">Description</h6>
              <p class="detail-value description-text">
                {{ acuityLevel.description || 'No description provided' }}
              </p>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="d-flex justify-content-between align-items-center">
            <small class="text-muted">Last updated: {{ $formatDateTime(acuityLevel.updated_at) }}</small>
            <button
              v-if="$canAccess('PatientActivityLevelEdit')"
              class="btn btn-sm"
              :class="acuityLevel.is_active ? 'btn-outline-danger' : 'btn-outline-success'"
              @click="toggleStatus"
            >
              {{ acuityLevel.is_active ? 'Deactivate' : 'Activate' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AcuityLevelView',
  data() {
    return {
      acuityLevel: null,
      loading: false,
      error: null
    }
  },
  methods: {
    getLevelBadgeClass(level) {
      switch (level) {
        case 1: return 'bg-success';
        case 2: return 'bg-info';
        case 3: return 'bg-warning';
        case 4: return 'bg-danger';
        case 5: return 'bg-dark';
        default: return 'bg-secondary';
      }
    },
    
    async fetchAcuityLevel() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get(`/patient-activity-levels/${this.$route.params.id}`);
        this.acuityLevel = response.data.data;
      } catch (error) {
        console.error('Error fetching acuity level:', error);
        this.error = error.response?.data?.message || 'Failed to load acuity level data. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    async toggleStatus() {
      const actionText = this.acuityLevel.is_active ? 'deactivate' : 'activate';
      
      if (!confirm(`Are you sure you want to ${actionText} this acuity level?`)) {
        return;
      }
      
      try {
        const response = await this.$api.post(`/patient-activity-levels/${this.acuityLevel.id}/toggle`);
        this.acuityLevel = response.data.data;
        this.$toast.success(response.data.message || `Acuity level ${this.acuityLevel.is_active ? 'activated' : 'deactivated'} successfully`);
      } catch (error) {
        console.error('Error toggling acuity level status:', error);
        this.$toast.error(error.response?.data?.message || `Failed to ${actionText} acuity level. Please try again.`);
      }
    }
  },
  mounted() {
    this.fetchAcuityLevel();
  }
}
</script>

<style scoped>
.acuity-level-view {
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.card {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border: none;
}

.detail-label {
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-size: 1.1rem;
}

.description-text {
  white-space: pre-line;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    width: 100%;
  }
}
</style>
